import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
import { paisa } from "./money.js";
/** Delivery zones and charges (O-02, docs/specs/shipping.md). */
export const DELIVERY_ZONE_STATUSES = ["active", "archived"];
export const MAX_ZONE_AREAS = 600;
const geoId = z.int().min(1).max(32_767);
export const deliveryZoneAreaSchema = z
    .object({
    districtId: geoId,
    /** Null: the whole district. */
    upazilaId: geoId.nullable().default(null),
})
    .strict();
export const deliveryZoneSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    baseChargePaisa: paisa(),
    freeWeightGrams: z.int(),
    weightStepGrams: z.int(),
    stepChargePaisa: paisa(),
    /** Used when no zone covers the address. */
    isDefault: z.boolean(),
    /** ST-07 (A6): usual delivery time in days, shown to shoppers (null: not given). */
    daysMin: z.int().nullable().default(null),
    daysMax: z.int().nullable().default(null),
    status: z.enum(DELIVERY_ZONE_STATUSES),
    areas: z.array(z.object({
        districtId: z.int(),
        upazilaId: z.int().nullable(),
        districtName: z.string(),
        upazilaName: z.string().nullable(),
    })),
    version: z.int(),
});
export const deliveryZoneListSchema = z.object({ data: z.array(deliveryZoneSchema) });
const zoneFields = {
    name: z.string().trim().min(1).max(100),
    baseChargePaisa: paisa().min(0).max(10_000_000),
    freeWeightGrams: z.int().min(0).max(1_000_000),
    weightStepGrams: z.int().min(1).max(1_000_000),
    stepChargePaisa: paisa().min(0).max(10_000_000),
    isDefault: z.boolean(),
    areas: z.array(deliveryZoneAreaSchema).max(MAX_ZONE_AREAS),
    daysMin: z.int().min(0).max(60).nullable(),
    daysMax: z.int().min(0).max(60).nullable(),
};
const daysInOrder = (z0) => z0.daysMin == null || z0.daysMax == null || z0.daysMin <= z0.daysMax;
export const createDeliveryZoneRequestSchema = z
    .object({
    ...zoneFields,
    freeWeightGrams: zoneFields.freeWeightGrams.default(1_000),
    weightStepGrams: zoneFields.weightStepGrams.default(1_000),
    stepChargePaisa: zoneFields.stepChargePaisa.default(0),
    isDefault: zoneFields.isDefault.default(false),
    areas: zoneFields.areas.default([]),
    daysMin: zoneFields.daysMin.default(null),
    daysMax: zoneFields.daysMax.default(null),
})
    .strict()
    .refine(daysInOrder, { message: "The first day must not be after the last", path: ["daysMax"] });
export const updateDeliveryZoneRequestSchema = z
    .object({
    name: zoneFields.name.optional(),
    baseChargePaisa: zoneFields.baseChargePaisa.optional(),
    freeWeightGrams: zoneFields.freeWeightGrams.optional(),
    weightStepGrams: zoneFields.weightStepGrams.optional(),
    stepChargePaisa: zoneFields.stepChargePaisa.optional(),
    isDefault: zoneFields.isDefault.optional(),
    status: z.enum(DELIVERY_ZONE_STATUSES).optional(),
    /** Replaces the zone's areas. */
    areas: zoneFields.areas.optional(),
    daysMin: zoneFields.daysMin.optional(),
    daysMax: zoneFields.daysMax.optional(),
    version: z.int().min(1),
})
    .strict();
export const deliveryQuoteQuerySchema = z.object({
    districtId: z.coerce.number().int().min(1).max(32_767).optional(),
    upazilaId: z.coerce.number().int().min(1).max(32_767).optional(),
    weightGrams: z.coerce.number().int().min(0).max(1_000_000).default(0),
});
export const deliveryQuoteSchema = z.object({
    /** Null when the tenant has no active zone at all. */
    zoneId: z.uuid().nullable(),
    zoneName: z.string().nullable(),
    chargePaisa: paisa(),
});
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "shipping" };
export const listDeliveryZones = defineEndpoint({
    ...tag,
    name: "listDeliveryZones",
    method: "GET",
    path: "/v1/delivery-zones",
    access: { permission: "orders.view" },
    summary: "Delivery zones (the three defaults are created on first use)",
    status: 200,
    response: deliveryZoneListSchema,
});
export const createDeliveryZone = defineEndpoint({
    ...tag,
    name: "createDeliveryZone",
    method: "POST",
    path: "/v1/delivery-zones",
    access: { permission: "settings.manage" },
    summary: "Add a delivery zone",
    body: createDeliveryZoneRequestSchema,
    status: 201,
    response: deliveryZoneSchema,
    errors: ["AREA_IN_OTHER_ZONE"],
});
export const updateDeliveryZone = defineEndpoint({
    ...tag,
    name: "updateDeliveryZone",
    method: "PATCH",
    path: "/v1/delivery-zones/{id}",
    access: { permission: "settings.manage" },
    summary: "Change a delivery zone's charges or areas, or archive it",
    params: idParamsSchema,
    body: updateDeliveryZoneRequestSchema,
    status: 200,
    response: deliveryZoneSchema,
    errors: ["CONFLICT", "AREA_IN_OTHER_ZONE"],
});
export const quoteDelivery = defineEndpoint({
    ...tag,
    name: "quoteDelivery",
    method: "GET",
    path: "/v1/delivery-zones/quote",
    access: { permission: "orders.create" },
    summary: "The zone and delivery charge for an address and weight",
    query: deliveryQuoteQuerySchema,
    status: 200,
    response: deliveryQuoteSchema,
});
//# sourceMappingURL=delivery-zones.js.map